import React, { useState } from "react";
import BohrModelVisualizer from "./BohrModelVisualizer";
import compounds from "./Compounds.json";
import CompoundBohrVisualizer from "./CompoundBohrVisualizer";

function getDonorLossShellConfig(element) {
  if (!element.shellConfig || element.shellConfig.length === 0) return [];
  const sc = [...element.shellConfig];
  for (let i = sc.length - 1; i >= 0; i--) {
    if (sc[i] > 0) {
      sc[i]--;
      if (sc[i] === 0 && i === sc.length - 1) sc.pop();
      break;
    }
  }
  return sc;
}
function getAcceptorGainShellConfig(element) {
  if (!element.shellConfig || element.shellConfig.length === 0) return [];
  const maxShell = [2, 8, 18, 32, 32, 18, 8];
  const sc = [...element.shellConfig];
  let idx = sc.length - 1;
  if (sc[idx] < maxShell[idx]) {
    sc[idx]++;
  } else if (sc.length < maxShell.length) {
    sc.push(1);
  }
  return sc;
}

export default function CompoundPractice({ leftElement, rightElement }) {
  const compound =
    compounds.find(
      c =>
        (c.elements.includes(leftElement.symbol) &&
          c.elements.includes(rightElement.symbol))
    ) || {};

  const [step, setStep] = useState(1);
  const [replayKey, setReplayKey] = useState(Date.now());

  // Defensive check
  if (!leftElement || !rightElement || !leftElement.shellConfig || !rightElement.shellConfig) {
    return <div style={{ padding: "2em", color: "#b44" }}>Loading atom data...</div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", position: "relative" }}>
      <div style={{ marginBottom: 28 }}>
        <b>Step {step} of 4</b>
      </div>

      {/* Step 1: Show Atoms */}
      {step === 1 && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 44 }}>
            <div>
              <BohrModelVisualizer element={leftElement} />
              <div style={{ textAlign: "center", marginTop: 4 }}>
                <b>{leftElement.symbol}</b>
              </div>
            </div>
            <span style={{ fontSize: "2em" }}>+</span>
            <div>
              <BohrModelVisualizer element={rightElement} />
              <div style={{ textAlign: "center", marginTop: 4 }}>
                <b>{rightElement.symbol}</b>
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", margin: "1.5em 0" }}>
            <button className="submit-btn" onClick={() => setStep(2)}>Next: Predict the Reaction</button>
          </div>
        </>
      )}

      {/* Step 2: Predict/Quiz */}
      {step === 2 && (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "1.1em", margin: "1em 0" }}>
            What type of bond will {leftElement.symbol} and {rightElement.symbol} form?
          </div>
          <button className="submit-btn" onClick={() => setStep(3)}>
            {compound.type ? (compound.type.charAt(0).toUpperCase() + compound.type.slice(1)) : "Ionic"}
          </button>
          <div style={{ marginTop: "2em" }}>
            <button className="next-btn" onClick={() => setStep(3)}>See the Combination</button>
          </div>
        </div>
      )}

      {/* Step 3: Show Combination / Animation */}
      {step === 3 && (
        <>
          {compound.type === "ionic" ? (
            <>
              <CompoundBohrVisualizer
                key={replayKey}
                donor={leftElement}
                acceptor={rightElement}
                showTransfer={true}
                donorLossShellConfig={getDonorLossShellConfig(leftElement)}
                acceptorGainShellConfig={getAcceptorGainShellConfig(rightElement)}
                animationDuration={2.5}
              />
              <div style={{textAlign:'center',marginTop:'1.2em'}}>
                <button className="submit-btn" onClick={() => setReplayKey(Date.now())}>Replay Animation</button>
              </div>
            </>
          ) : (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 80,
              minHeight: 320,
              marginBottom: 18,
              marginTop: 18
            }}>
              {/* Optional: static Bohr models for covalent compounds */}
            </div>
          )}
          <div style={{ textAlign: "center" }}>
            <button className="next-btn" onClick={() => setStep(4)}>See Explanation & Formula</button>
          </div>
        </>
      )}

      {/* Step 4: Show Explanation, Formula, and Real-World Use */}
      {step === 4 && (
        <div style={{
          textAlign: "center",
          background: "#f8f8fb",
          borderRadius: 14,
          padding: "1.5em 1em",
          boxShadow: "0 2px 10px #b9e6ff38",
          maxWidth: 440,
          margin: "2em auto"
        }}>
          <h2>{compound.formula || `${leftElement.symbol}${rightElement.symbol}`}</h2>
          <div style={{ fontSize: "1.14em", margin: "1em 0" }}>
            {compound.explanation ||
              `When ${leftElement.name} and ${rightElement.name} combine, an electron is transferred and an ionic bond is formed.`}
          </div>
          {compound.uses && (
            <div style={{
              fontSize: "1em",
              color: "#36718d",
              marginTop: 12,
              background: "#eaf9fd",
              borderRadius: 8,
              padding: "0.7em"
            }}>
              <b>Real-World Use:</b> {compound.uses}
            </div>
          )}
          <div style={{ marginTop: "2.1em" }}>
            <button className="next-btn" onClick={() => setStep(1)}>Try Another Compound</button>
          </div>
        </div>
      )}
    </div>
  );
}
